import React, { useEffect, useState } from "react";
import { FileSignature, Sparkles } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

const STATUSES = ["Pending", "Approved", "Denied", "Resubmitted", "Escalated"];

export default function PriorAuth() {
  const [clients, setClients] = useState([]);
  const [auths, setAuths] = useState([]);
  const [form, setForm] = useState({ client_id: "", service_level: "Outpatient Individual 16 sessions", payer: "", justification_input: "" });
  const [gen, setGen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/prior-auths").then(({ data }) => setAuths(data));
  }, []);

  const generate = async () => {
    if (!form.client_id || !form.payer) return toast.error("Select client and payer");
    setGen(true);
    try {
      const { data } = await api.post("/prior-auths/generate", form);
      setAuths([data, ...auths]);
      setActive(data);
      toast.success("Justification letter generated");
    } catch { toast.error("Failed"); }
    setGen(false);
  };

  const updateStatus = async (id, status) => {
    await api.put(`/prior-auths/${id}`, { status });
    setAuths(auths.map(a => a.id === id ? { ...a, status } : a));
    toast.success("Status updated");
  };

  const generateAppeal = async (id) => {
    try {
      const { data } = await api.post(`/prior-auths/${id}/appeal`);
      toast.success("Appeal generated");
      const updated = auths.map(a => a.id === id ? { ...a, appeal_letter: data.appeal_letter, status: "Resubmitted" } : a);
      setAuths(updated);
      setActive(updated.find(a => a.id === id));
    } catch { toast.error("Appeal failed"); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page title="Prior Authorization" subtitle="AI-drafted payer letters and denial appeals.">
      <div className="grid lg:grid-cols-2 gap-5">
        <Card delay={0.05} testid="auth-composer">
          <h2 className="font-serif text-2xl mb-4">New Authorization Request</h2>
          <div className="space-y-3">
            <select value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="auth-client-select">
              <option value="">Select client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
            <input placeholder="Payer (e.g. Aetna, BCBS, Cigna)" value={form.payer} onChange={set("payer")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="auth-payer" />
            <input placeholder="Service level" value={form.service_level} onChange={set("service_level")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="auth-service-level" />
            <textarea placeholder="Clinical details: symptoms, functional impairment, treatment history, expected outcomes" value={form.justification_input} onChange={set("justification_input")} rows={5} className="w-full px-4 py-3 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="auth-details"></textarea>
            <PrimaryButton onClick={generate} disabled={gen} testid="auth-generate-btn">
              {gen ? "Drafting…" : <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Draft Letter</span>}
            </PrimaryButton>
          </div>
        </Card>

        <Card delay={0.1} testid="auth-preview">
          <h2 className="font-serif text-2xl mb-4">Letter Preview</h2>
          {active ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Pill variant={active.status === "Approved" ? "success" : active.status === "Denied" ? "danger" : "warn"}>{active.status}</Pill>
                <select value={active.status} onChange={(e) => updateStatus(active.id, e.target.value)} className="text-xs px-3 py-1 rounded-full outline-none" style={{ background: "var(--mf-muted)" }} data-testid="auth-status-select">
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="text-sm whitespace-pre-wrap mf-prose max-h-96 overflow-y-auto p-4 rounded-xl" style={{ background: "var(--mf-muted)" }}>{active.letter}</div>
              {active.status === "Denied" && !active.appeal_letter && (
                <PrimaryButton onClick={() => generateAppeal(active.id)} testid="auth-appeal-btn">Generate Appeal Letter</PrimaryButton>
              )}
              {active.appeal_letter && (
                <div className="text-sm whitespace-pre-wrap mf-prose max-h-96 overflow-y-auto p-4 rounded-xl border" style={{ borderColor: "var(--mf-accent)", background: "rgba(201,168,76,0.08)" }}>
                  <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--mf-accent)" }}>Appeal Letter</div>
                  {active.appeal_letter}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm py-12 text-center" style={{ color: "var(--mf-text-muted)" }}>Select or generate a letter to preview.</div>
          )}
        </Card>
      </div>

      <div className="mt-8">
        <h2 className="font-serif text-2xl mb-4">All Authorizations</h2>
        {auths.length === 0 ? <EmptyState icon={FileSignature} title="No authorizations yet" description="Draft your first prior-auth letter above." /> : (
          <Card>
            <div className="space-y-2">
              {auths.map((a) => (
                <div key={a.id} className="p-3 rounded-xl flex items-center justify-between cursor-pointer hover:bg-black/[0.03]" onClick={() => setActive(a)} data-testid={`auth-item-${a.id}`} style={{ background: "var(--mf-muted)" }}>
                  <div>
                    <div className="font-medium text-sm">{a.payer} · {a.service_level}</div>
                    <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <Pill variant={a.status === "Approved" ? "success" : a.status === "Denied" ? "danger" : "warn"}>{a.status}</Pill>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}
